import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import { LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { TrustSignals } from '../components/TrustSignals';
import { DegreeCategoryGrid } from '../components/DegreeCategoryGrid';
import { SEOContentSection } from '../components/SEOContentSection';
import { FAQSection } from '../components/FAQSection';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { focusOptions, specializationOptions } from '../data/degrees';
export function HomePage() {
  const {
    areaStudy,
    program
  } = useParams<{
    areaStudy?: string;
    program?: string;
  }>();
  // The same component serves "/" and the "/degree/..." templates, so the
  // template routes describe their own program rather than repeating the
  // homepage's title and description.
  const currentYear = new Date().getFullYear();
  const routeAreaName = focusOptions.find(f => f.value === areaStudy)?.type;
  const routeProgramName = specializationOptions.find(s => s.value === program)?.type;
  const routeSubject = routeProgramName && !/^(General|All )/i.test(routeProgramName) ? routeProgramName : routeAreaName;
  const metaTitle = routeSubject ? `Best Online ${routeSubject} Degree Programs of ${currentYear} | findonlinecollege.com` : `findonlinecollege.com | Compare Accredited Online Degree Programs ${currentYear}`;
  const metaDescription = routeSubject ? `Compare accredited online ${routeSubject.toLowerCase()} degree programs. See schools matched to your goals, schedule, and budget — free, with no obligation to enroll.` : 'Compare accredited online colleges and universities. Get matched with degree programs that fit your career goals, schedule, and budget. 100% free service.';
  usePageMeta(metaTitle, metaDescription, {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website',
    // The optional third URL segment only pre-selects the degree level, so those
    // deeper URLs consolidate onto the two-segment program page.
    canonical: areaStudy && program ? `/degree/${areaStudy}/${program}` : '/'
  });
  const navigate = useNavigate();
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Track selected values for the content section
  const [selectedDegree, setSelectedDegree] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  // On narrow screens the hero and its widget fill the viewport, so results
  // rendered below it are invisible without scrolling — the visitor submits and
  // appears to get nothing back. Bring the matches into view once they arrive.
  const resultsRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const fetchResults = useCallback(async (degreeInterest: string, areaStudyStr: string, mProgram: string) => {
    // Only search if mProgram (specialization) is selected
    if (!mProgram) {
      setHasSearched(false);
      return;
    }
    setSelectedDegree(degreeInterest);
    setSelectedArea(areaStudyStr);
    setSelectedProgram(mProgram);
    // Update URL if not already matching
    if (areaStudyStr && mProgram && (areaStudy !== areaStudyStr || program !== mProgram)) {
      navigate(`/degree/${areaStudyStr}/${mProgram}`, {
        replace: true
      });
    }
    pendingScrollRef.current = true;
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudyStr || undefined,
      mProgram: mProgram || undefined,
      // The /degree/ template pages have no questionnaire answers to draw on, so
      // the clicks search sends the standard profile Media Matchers expects for
      // an unqualified visitor.
      gradYear: '2020',
      startTimeframe: '1',
      highestEducation: '1'
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, [navigate, areaStudy, program]);
  // On mount, if params exist, search
  useEffect(() => {
    if (areaStudy && program) {
      fetchResults('', areaStudy, program);
    }
  }, [areaStudy, program, fetchResults]);
  // Mobile only — on desktop the results already sit beside the fold.
  useEffect(() => {
    if (isLoading || !hasSearched || !pendingScrollRef.current) return;
    const target = resultsRef.current;
    if (!target || window.innerWidth >= 1024) {
      pendingScrollRef.current = false;
      return;
    }
    pendingScrollRef.current = false;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isLoading, hasSearched]);
  return <main className="flex-grow bg-background" data-id="element-2074" data-mp="1l04wrnriw4z3">
      <HeroSection onSelectionChange={fetchResults} data-id="element-2075" data-mp="1kytujsdq75a3" />
      <TrustSignals data-id="element-2076" data-mp="3hsooymm12pw1" />

      {hasSearched ? <div ref={resultsRef} id="school-matches" className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 scroll-mt-20" data-id="element-2077" data-mp="3htpzmpz4tad8">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-2078" data-mp="3mst52bj0xilq">
            {/* Left Column: Results */}
            <div className="lg:w-2/3" data-id="element-2079" data-mp="tycg3aqj37q3">
              {/* Loading State */}
              {isLoading && <div className="flex flex-col items-center justify-center py-16 bg-surface rounded-2xl border border-hairline shadow-ds" data-id="element-2080" data-mp="ozhlzcn41wqo">
                  <LoaderIcon className="w-8 h-8 text-primary animate-spin mb-4" data-id="element-2081" data-mp="xxl7wa20cvj0" />
                  <p className="text-ink-secondary font-medium" data-id="element-2082" data-mp="1cv3wnceor8qg">
                    Finding your best school matches...
                  </p>
                </div>}

              {/* Error State */}
              {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3" data-id="element-2083" data-mp="1lnxijson16da">
                  <AlertCircleIcon className="w-5 h-5 text-error flex-shrink-0 mt-0.5" data-id="element-2084" data-mp="1hn57ctw7gred" />
                  <div data-id="element-2085" data-mp="1vl269wy4t9w7">
                    <p className="text-ink font-semibold" data-id="element-2086" data-mp="1vmd7bp0703jm">{apiError}</p>
                    <p className="text-ink-secondary text-sm mt-1" data-id="element-2087" data-mp="1qnju7xotiejz">
                      Try adjusting your search criteria or check back later.
                    </p>
                  </div>
                </div>}

              {/* Results */}
              {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-2088" data-mp="20b5vaxxlhjrd">
                  {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-2089" data-mp="2597hvsu8c17l" data-mpi={idx}>
                      <MatchedSchoolCard school={school} index={idx} data-id="element-2090" data-mp="1rhj3fiwid0ng" />
                      {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-2091" data-mp="1w913m4qpix7d" />}
                    </Fragment>)}
                </div>}

              {/* Empty State */}
              {!isLoading && !apiError && matchedSchools.length === 0 && <div className="bg-surface rounded-2xl border border-hairline shadow-ds p-12 text-center" data-id="element-2092" data-mp="1mig84q6by8so">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-background-alt mb-4" data-id="element-2093" data-mp="18iyenxi3a7cz">
                    <AlertCircleIcon className="w-8 h-8 text-primary" data-id="element-2094" data-mp="1cjie57q4xn2a" />
                  </div>
                  <h3 className="text-xl font-bold text-ink mb-2" data-id="element-2095" data-mp="1syzwsl8e98ry">
                    No matches found
                  </h3>
                  <p className="text-ink-secondary max-w-md mx-auto" data-id="element-2096" data-mp="1nz554fgsof5u">
                    Try adjusting your degree level or program selection to find
                    more results.
                  </p>
                </div>}
            </div>

            {/* Right Column: Inline Lead Capture */}
            <div className="lg:w-1/3" data-id="element-2097" data-mp="1a3jk0jdomfip">
              <HomeInlineLeadCapture data-id="element-2098" data-mp="15a6wkckqoerj" />
            </div>
          </div>

          {/* Dynamic Program Content Section */}
          {!isLoading && !apiError && selectedProgram && <ProgramContentSection degreeInterest={selectedDegree} areaOfStudy={selectedArea} specialization={selectedProgram} data-id="element-2099" data-mp="109atu2c4iynp" />}
        </div> : <>
          <SEOContentSection data-id="element-2100" data-mp="qcei7nqxhwex" />
          <DegreeCategoryGrid data-id="element-2101" data-mp="qco8zdjss7lo" />
          <FAQSection data-id="element-2102" data-mp="mbukoj2g0qs9" />
        </>}
    </main>;
}